import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { X } from 'lucide-preact';
import { toCssHex } from '../../sim/color.ts';
import {
  MAX_LAPS,
  MIN_LAPS,
  formatRouteMetres,
  routeLengthM,
  type EditorTool,
} from '../../sim/map/index.ts';
import { ColorPicker, LiveSlider, NumberField } from '../controls.tsx';
import { LightingLookGroup } from '../lookSettings.tsx';
import { Tooltip } from '../tooltip.tsx';
import { EditorIcon, RedoIcon, UndoIcon } from './icons.tsx';
import { EnvironmentEditor } from './environment.tsx';
import { MapNameField } from './mapName.tsx';
import { MapSettings } from './mapSettings.tsx';
import { ShortcutList } from './shortcuts.tsx';
import type { EditorToolbarActions, EditorToolbarState } from './toolbar.tsx';
import type { EditorLogBadge } from './log.ts';
import { toolsInMenu } from './tools.ts';

type MenuId =
  | 'file'
  | 'edit'
  | 'map'
  | 'terrain'
  | 'water'
  | 'environment'
  | 'lighting'
  | 'race'
  | 'routes'
  | 'view'
  | 'log';

function Menu({
  id,
  label,
  open,
  wide,
  badge,
  onToggle,
  onHover,
  children,
}: {
  id: MenuId;
  label: string;
  open: boolean;
  wide?: boolean;
  badge?: EditorLogBadge | null;
  onToggle: () => void;
  onHover: () => void;
  children: ComponentChildren;
}): JSX.Element {
  return (
    <div class="editor-menu">
      <button
        type="button"
        class="editor-menu-trigger"
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={`editor-menu-${id}`}
        data-log-badge={badge ?? undefined}
        onClick={onToggle}
        onPointerEnter={onHover}
      >
        {label}
        {badge ? (
          <span class="editor-log-dot" data-severity={badge} aria-hidden="true" />
        ) : null}
      </button>
      <div
        id={`editor-menu-${id}`}
        class={`editor-menu-panel ui-panel${wide ? ' is-wide' : ''}`}
        role="menu"
        hidden={!open}
      >
        {children}
      </div>
    </div>
  );
}

function MenuItem({
  label,
  shortcut,
  disabled,
  pressed,
  fileAction,
  dataAttr,
  onClick,
}: {
  label: string;
  shortcut?: string;
  disabled?: boolean;
  pressed?: boolean;
  fileAction?: string;
  dataAttr?: string;
  onClick: () => void;
}): JSX.Element {
  const dataProps = dataAttr ? { [dataAttr]: '' } : {};

  return (
    <button
      type="button"
      class="editor-menu-item"
      role="menuitem"
      disabled={disabled}
      aria-pressed={pressed === undefined ? undefined : pressed ? 'true' : 'false'}
      data-file-action={fileAction}
      {...dataProps}
      onClick={onClick}
    >
      <span>{label}</span>
      {shortcut ? <span class="editor-toolbar-shortcut">{shortcut}</span> : null}
    </button>
  );
}

/** Survives a toolbar sync remount so a View switch can stay open. */
let persistedOpenMenu: MenuId | null = null;

export function resetEditorMenuOpen(): void {
  persistedOpenMenu = null;
}

export function EditorMenuBar({
  state,
  actions,
}: {
  state: EditorToolbarState;
  actions: EditorToolbarActions;
}): JSX.Element {
  const [open, setOpenState] = useState<MenuId | null>(persistedOpenMenu);
  const [keysOpen, setKeysOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  // A hover-opened menu was not clicked open, so the click that lands on the same trigger
  // should confirm it open rather than immediately toggle it shut.
  const openedByHover = useRef(false);

  const setOpen = (next: MenuId | null | ((current: MenuId | null) => MenuId | null)): void => {
    setOpenState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      persistedOpenMenu = value;
      return value;
    });
  };

  useEffect(() => {
    if (open !== 'file') {
      setRecentOpen(false);
    }

    if (open !== 'map') {
      setTestOpen(false);
    }

    if (open === null) {
      return;
    }

    const onPointerDown = (event: PointerEvent): void => {
      const node = barRef.current;

      if (node && !node.contains(event.target as Node)) {
        setOpen(null);
      }
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(null);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (id: MenuId): void => {
    const wasHover = openedByHover.current;
    openedByHover.current = false;
    setOpen((current) => {
      if (current === id) {
        // Hover already opened it on the way to this click; keep it open.
        return wasHover ? id : null;
      }
      return id;
    });
  };

  // Hovering a trigger opens it only once the menubar is already engaged, matching the
  // standard menubar feel: click one menu open, then slide across the others.
  const hover = (id: MenuId): void => {
    if (open === null || open === id) {
      return;
    }
    openedByHover.current = true;
    setOpen(id);
  };

  const close = (): void => {
    openedByHover.current = false;
    setOpen(null);
  };

  const chooseTool = (tool: EditorTool): void => {
    persistedOpenMenu = null;
    actions.setTool(tool);
    close();
  };

  const run = (action: () => void): void => {
    action();
    close();
  };

  return (
    <div ref={barRef} class="editor-menubar ui-panel ui-chrome">
      <div class="editor-menubar-left">
      <div class="editor-menubar-menus" role="menubar" aria-label="Editor">
        <Menu
          id="file"
          label="File"
          open={open === 'file'}
          onToggle={() => toggle('file')}
          onHover={() => hover('file')}
        >
          <MenuItem label="New" onClick={() => run(actions.onNew)} />
          <MenuItem label="Open" fileAction="open" onClick={() => run(actions.onOpen)} />
          {state.recentMaps.length === 0 ? (
            <MenuItem label="Open Recent" fileAction="open-recent" disabled onClick={() => undefined} />
          ) : (
            <>
              <button
                type="button"
                class="editor-menu-item"
                role="menuitem"
                aria-expanded={recentOpen ? 'true' : 'false'}
                aria-controls="editor-open-recent"
                data-file-action="open-recent"
                onClick={() => setRecentOpen(!recentOpen)}
              >
                <span>Open Recent</span>
                <span class="editor-dock-chevron" aria-hidden="true">
                  ▾
                </span>
              </button>
              <div id="editor-open-recent" class="editor-menu-recent" hidden={!recentOpen}>
                {state.recentMaps.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    class="editor-menu-item is-nested"
                    role="menuitem"
                    data-recent-map={entry.id}
                    onClick={() => run(() => actions.onOpenRecent(entry.id))}
                  >
                    <span>{entry.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <MenuItem label="Import" fileAction="import" onClick={() => run(actions.onImport)} />
          <MenuItem
            label="Load Reference Image"
            fileAction="load-reference-image"
            onClick={() => run(actions.onLoadReferenceImage)}
          />
          <MenuItem
            label="Clear Reference Image"
            fileAction="clear-reference-image"
            disabled={!state.hasReferenceImage}
            onClick={() => run(actions.onClearReferenceImage)}
          />
          <MenuItem
            label="Save"
            shortcut="Ctrl+S"
            fileAction="save"
            onClick={() => run(actions.onSave)}
          />
          <MenuItem
            label="Save as"
            shortcut="Ctrl+Shift+S"
            fileAction="save-as"
            onClick={() => run(actions.onSaveAs)}
          />
          <MenuItem
            label="Save and Close"
            fileAction="save-and-close"
            onClick={() => run(actions.onSaveAndClose)}
          />
          <MenuItem label="Export" onClick={() => run(actions.onExport)} />
          <MenuItem
            label="Delete"
            fileAction="delete"
            disabled={!state.canDeleteMap}
            onClick={() => run(actions.onDeleteMap)}
          />
        </Menu>

        <Menu
          id="edit"
          label="Edit"
          open={open === 'edit'}
          onToggle={() => toggle('edit')}
          onHover={() => hover('edit')}
        >
          <MenuItem
            label="Undo"
            shortcut="Ctrl+Z"
            disabled={!state.canUndo}
            onClick={() => run(actions.onUndo)}
          />
          <MenuItem
            label="Redo"
            shortcut="Ctrl+Y"
            disabled={!state.canRedo}
            onClick={() => run(actions.onRedo)}
          />
          <button
            type="button"
            class="editor-menu-item editor-menu-keys"
            aria-expanded={keysOpen ? 'true' : 'false'}
            aria-controls="editor-shortcut-hint"
            onClick={() => setKeysOpen(!keysOpen)}
          >
            <span>Keys</span>
            <span class="editor-dock-chevron" aria-hidden="true">
              ▾
            </span>
          </button>
          <div hidden={!keysOpen}>
            <ShortcutList />
          </div>
        </Menu>

        <Menu
          id="map"
          label="Map"
          open={open === 'map'}
          wide
          onToggle={() => toggle('map')}
          onHover={() => hover('map')}
        >
          <MenuItem
            label="Test drive"
            disabled={!state.playable}
            dataAttr="data-test-drive"
            pressed={testOpen}
            onClick={() => {
              if (!state.playable) {
                return;
              }

              setTestOpen((openNow) => !openNow);
              actions.refreshTestBoats();
            }}
          />
          {testOpen && state.playable
            ? state.testBoats.map((boat) => (
                <MenuItem
                  key={boat.id}
                  label={boat.label}
                  disabled={boat.disabled}
                  onClick={() => run(() => actions.onTestBoat(boat.id))}
                />
              ))
            : null}
          <div class="editor-menu-rule" aria-hidden="true" />
          <MapSettings
            map={actions.getMap()}
            onSetMapMeta={actions.onSetMapMeta}
            onSetMapSize={actions.onSetMapSize}
          />
        </Menu>

        <Menu
          id="terrain"
          label="Terrain"
          open={open === 'terrain'}
          onToggle={() => toggle('terrain')}
          onHover={() => hover('terrain')}
        >
          {toolsInMenu('terrain').map((def) => (
            <MenuItem
              key={def.tool}
              label={def.label}
              shortcut={def.shortcut}
              pressed={state.tool === def.tool}
              disabled={def.tool === 'place-prop' && state.showElevation}
              onClick={() => chooseTool(def.tool)}
            />
          ))}
        </Menu>

        <Menu
          id="water"
          label="Water"
          open={open === 'water'}
          wide
          onToggle={() => toggle('water')}
          onHover={() => hover('water')}
        >
          {toolsInMenu('water').map((def) => (
            <MenuItem
              key={def.tool}
              label={def.label}
              shortcut={def.shortcut}
              pressed={state.tool === def.tool}
              onClick={() => chooseTool(def.tool)}
            />
          ))}
          <div class="editor-menu-look">
            <ColorPicker
              label="Watercolor"
              value={state.look.waterColor}
              onChange={(waterColor) => actions.onLookLive({ waterColor })}
              onCommit={actions.onLookCommit}
              onCancel={actions.onLookCancel}
            />
            <LiveSlider
              label="Water alpha"
              value={state.look.waterColor.a}
              dataAttr="data-water-alpha"
              onInput={(a) => actions.onLookLive({ waterColor: { ...state.look.waterColor, a } })}
              onCommit={actions.onLookCommit}
            />
          </div>
        </Menu>

        <Menu
          id="environment"
          label="Environment"
          open={open === 'environment'}
          wide
          onToggle={() => toggle('environment')}
          onHover={() => hover('environment')}
        >
          <EnvironmentEditor
            environment={actions.getMap().environment}
            onChange={actions.onSetEnvironment}
          />
        </Menu>

        <Menu
          id="lighting"
          label="Lighting"
          open={open === 'lighting'}
          wide
          onToggle={() => toggle('lighting')}
          onHover={() => hover('lighting')}
        >
          <Tooltip
            label="Sun lighting on the water from island and rock elevation"
            side="right"
          >
            <MenuItem
              label="Preview lighting"
              pressed={state.previewLighting}
              dataAttr="data-preview-lighting"
              onClick={() => actions.setPreviewLighting(!state.previewLighting)}
            />
          </Tooltip>
          {state.previewLighting ? (
            <div class="editor-menu-look">
              <LightingLookGroup
                sun={state.sun}
                look={state.look}
                showSun
                showWater={false}
                showGridControls={false}
                onSunLive={actions.onSunLive}
                onSunCommit={actions.onSunCommit}
                onLookLive={actions.onLookLive}
                onLookCommit={actions.onLookCommit}
                onLookCancel={actions.onLookCancel}
              />
            </div>
          ) : null}
        </Menu>

        <Menu
          id="race"
          label="Race"
          open={open === 'race'}
          wide
          onToggle={() => toggle('race')}
          onHover={() => hover('race')}
        >
          <div class="editor-map-settings">
            <NumberField
              label="Laps"
              value={actions.getMap().laps}
              min={MIN_LAPS}
              max={MAX_LAPS}
              step={1}
              onChange={(laps) => actions.onSetLaps(laps)}
            />
            <p class="ui-hint">
              The checkered line closes each lap. The last crossing finishes the race.
            </p>
          </div>
          <div class="editor-menu-rule" aria-hidden="true" />
          {toolsInMenu('race').map((def) => (
            <MenuItem
              key={def.tool}
              label={def.label}
              shortcut={def.shortcut}
              pressed={state.tool === def.tool}
              onClick={() => chooseTool(def.tool)}
            />
          ))}
          <div class="editor-menu-look">
            {(
              [
                ['movement', 'Movement circle', 'Movement alpha', 'data-movement-alpha'],
                ['prediction', 'Prediction circle', 'Prediction alpha', 'data-prediction-alpha'],
                ['moveLine', 'Move line', 'Move line alpha', 'data-move-line-alpha'],
                ['dest', 'Dest', 'Dest alpha', 'data-dest-alpha'],
              ] as const
            ).map(([key, colorLabel, alphaLabel, dataAttr]) => {
              const value = state.look.aimChrome[key];

              return (
                <div key={key}>
                  <ColorPicker
                    label={colorLabel}
                    value={value}
                    onChange={(next) => actions.onAimChromeLive({ [key]: next })}
                    onCommit={actions.onLookCommit}
                    onCancel={actions.onLookCancel}
                  />
                  <LiveSlider
                    label={alphaLabel}
                    value={value.a}
                    dataAttr={dataAttr}
                    onInput={(a) =>
                      actions.onAimChromeLive({
                        [key]: { ...value, a },
                      })
                    }
                    onCommit={actions.onLookCommit}
                  />
                </div>
              );
            })}
          </div>
        </Menu>

        <Menu
          id="routes"
          label="Routes"
          open={open === 'routes'}
          wide
          onToggle={() => toggle('routes')}
          onHover={() => hover('routes')}
        >
          <MenuItem
            label="New Route"
            shortcut="O"
            pressed={state.routeDrafting}
            dataAttr="data-new-route"
            onClick={() => chooseTool('draw-route')}
          />
          <MenuItem
            label="Show routes"
            pressed={state.showRoutes}
            dataAttr="data-show-routes-toggle"
            onClick={() => actions.setShowRoutes(!state.showRoutes)}
          />
          {actions.getMap().routes.map((route) => {
            const visible = !state.hiddenRouteIds.includes(route.id);
            const length = formatRouteMetres(routeLengthM(route.points, route.cornerRadius));

            return (
              <div
                key={route.id}
                class="editor-route-row"
                data-route-id={route.id}
                data-visible={visible ? 'true' : 'false'}
              >
                <button
                  type="button"
                  class="editor-menu-item editor-route-pick"
                  role="menuitem"
                  aria-pressed={visible ? 'true' : 'false'}
                  aria-label={`${length}`}
                  onClick={() => {
                    actions.onToggleRouteVisibility(route.id);
                  }}
                >
                  <span
                    class="editor-route-swatch"
                    style={{ background: toCssHex(route.color) }}
                    aria-hidden="true"
                  />
                  <span>{length}</span>
                </button>
                <button
                  type="button"
                  class="editor-route-delete"
                  aria-label={`Delete ${length} route`}
                  onClick={() => actions.onDeleteRoute(route.id)}
                >
                  <X size={14} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </Menu>

        <Menu
          id="view"
          label="View"
          open={open === 'view'}
          wide
          onToggle={() => toggle('view')}
          onHover={() => hover('view')}
        >
          <MenuItem
            label="Snap to Grid"
            shortcut="G"
            pressed={state.snapToGrid}
            onClick={() => actions.setSnapToGrid(!state.snapToGrid)}
          />
          <MenuItem
            label="Scale"
            pressed={state.showScale}
            onClick={() => actions.setShowScale(!state.showScale)}
          />
          <Tooltip
            label="Hold B and scroll to turn it. Hull at true size, 30 px to the right of the cursor"
            side="right"
          >
            <MenuItem
              label="Boat"
              shortcut="B"
              pressed={state.showBoat}
              dataAttr="data-show-boat"
              onClick={() => actions.setShowBoat(!state.showBoat)}
            />
          </Tooltip>
          <MenuItem
            label="Minimap"
            pressed={state.showMinimap}
            onClick={() => actions.setShowMinimap(!state.showMinimap)}
          />
          <Tooltip label="Frame budget: JS, GPU, draw calls, upload" side="right">
            <MenuItem
              label="Perf"
              pressed={state.showPerf}
              dataAttr="data-show-perf"
              onClick={() => actions.setShowPerf(!state.showPerf)}
            />
          </Tooltip>
          <Tooltip label="2.5-D island and rock columns as in play" side="right">
            <MenuItem
              label="Elevation"
              shortcut="E"
              pressed={state.showElevation}
              dataAttr="data-preview-elevation"
              onClick={() => actions.setShowElevation(!state.showElevation)}
            />
          </Tooltip>
          <Tooltip label="Organic paint on extruded caps. Needs Elevation." side="right">
            <MenuItem
              label="Cap flock"
              pressed={state.showCapFlock}
              dataAttr="data-cap-flock"
              onClick={() => actions.setShowCapFlock(!state.showCapFlock)}
            />
          </Tooltip>
          {state.showCapFlock ? (
            <div class="editor-menu-look">
              <LiveSlider
                label="Cap flock"
                minLabel="Less"
                maxLabel="More"
                value={state.capFlockAmount}
                dataAttr="data-cap-flock-amount"
                hint="How many ovals sit on each extruded deck"
                onInput={actions.setCapFlockAmount}
                onCommit={actions.onLookCommit}
              />
            </div>
          ) : null}
          <div class="editor-menu-look">
            <LiveSlider
              label="Waterline hint"
              value={state.waterlineHintAlpha}
              dataAttr="data-waterline-hint-alpha"
              onInput={actions.setWaterlineHintAlpha}
              onCommit={actions.onLookCommit}
            />
          </div>
          <MenuItem
            label="Grid"
            pressed={state.showGrid}
            dataAttr="data-show-grid"
            onClick={() => actions.setShowGrid(!state.showGrid)}
          />
          {state.showGrid ? (
            <div class="editor-menu-look">
              <LightingLookGroup
                sun={state.sun}
                look={state.look}
                showSun={false}
                showWater={false}
                showGridControls
                gridColorLabel="GridColor"
                onSunLive={actions.onSunLive}
                onSunCommit={actions.onSunCommit}
                onLookLive={actions.onLookLive}
                onLookCommit={actions.onLookCommit}
                onLookCancel={actions.onLookCancel}
              />
            </div>
          ) : null}
          {state.hasReferenceImage ? (
            <>
              <MenuItem
                label="Reference Image"
                pressed={state.showReferenceImage}
                dataAttr="data-show-reference-image"
                onClick={() => actions.setShowReferenceImage(!state.showReferenceImage)}
              />
              {state.showReferenceImage ? (
                <div class="editor-menu-look">
                  <LiveSlider
                    label="Reference alpha"
                    value={state.referenceImageAlpha}
                    dataAttr="data-reference-image-alpha"
                    hint="Opacity of the tracing overlay"
                    onInput={actions.setReferenceImageAlpha}
                    onCommit={actions.onReferenceImageAlphaCommit}
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </Menu>

        <Menu
          id="log"
          label="Log"
          open={open === 'log'}
          badge={state.logBadge}
          onToggle={() => toggle('log')}
          onHover={() => hover('log')}
        >
          <MenuItem
            label="Show log"
            pressed={state.logVisible}
            onClick={() => actions.setLogVisible(!state.logVisible)}
          />
          <MenuItem
            label="Errors"
            pressed={state.logFilter === 'errors'}
            onClick={() => actions.setLogFilter('errors')}
          />
          <MenuItem
            label="Warnings"
            pressed={state.logFilter === 'warnings'}
            onClick={() => actions.setLogFilter('warnings')}
          />
          <MenuItem
            label="Both"
            pressed={state.logFilter === 'both'}
            onClick={() => actions.setLogFilter('both')}
          />
        </Menu>
      </div>

      <div class="editor-menubar-history">
        <Tooltip label="Undo" shortcut="Ctrl+Z">
          <button
            type="button"
            class="editor-rail-button"
            aria-label="Undo"
            disabled={!state.canUndo}
            onClick={actions.onUndo}
          >
            <EditorIcon icon={UndoIcon} />
          </button>
        </Tooltip>
        <Tooltip label="Redo" shortcut="Ctrl+Y">
          <button
            type="button"
            class="editor-rail-button"
            aria-label="Redo"
            disabled={!state.canRedo}
            onClick={actions.onRedo}
          >
            <EditorIcon icon={RedoIcon} />
          </button>
        </Tooltip>
      </div>
      </div>

      <div class="editor-menubar-center">
        <MapNameField name={state.mapName} fileName={state.fileName} onRename={actions.onRename} />
      </div>

      <p class="editor-menubar-brand">Helmway Editor</p>
    </div>
  );
}
